/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { ExternalLink, RadioButtonGroup, RadioButton } from "@octopusdeploy/design-system-components";
import { ParseHelper, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { RetentionPeriodStrategy, RetentionUnit, type RunbookRetentionPolicy } from "@octopusdeploy/octopus-server-client";
import { cloneDeep } from "lodash";
import pluralize from "pluralize";
import React from "react";
import ComponentRow from "~/components/ComponentRow";
import { Text, ExpandableFormSection, minimum, Note, Summary } from "~/components/form";
interface RunbookRetentionPolicyControlInternalProps {
    retentionPolicy: RunbookRetentionPolicy;
    defaultRetentionPolicy?: RunbookRetentionPolicy;
    onRetentionPolicyChange(policy: RunbookRetentionPolicy): void;
}
class RunbookRetentionPolicyControlInternal extends React.Component<RunbookRetentionPolicyControlInternalProps> {
    updateRetentionPolicy(updater: (policy: RunbookRetentionPolicy) => void) {
        const policy = cloneDeep(this.props.retentionPolicy);
        updater(policy);
        this.props.onRetentionPolicyChange(policy);
    }
    render() {
        return (<ExpandableFormSection errorKey="retentionPolicy" title="Retention Policy" summary={Summary.summary(retentionPolicySummary(this.props.retentionPolicy))} help="Change the retention policy.">
                <RadioButtonGroup value={this.props.retentionPolicy.Strategy} onChange={(val) => this.updateRetentionPolicy((pol) => {
                pol.Strategy = val;
                pol.QuantityToKeep = val === RetentionPeriodStrategy.Forever ? 0 : 100;
                pol.ShouldKeepForever = val === RetentionPeriodStrategy.Forever;
            })} title="How many runs should we keep per environment?">
                    <RadioButton value={RetentionPeriodStrategy.Default} label={`Use default retention policy ${defaultRetentionPolicySummary(this.props.defaultRetentionPolicy)}`} isDefault={true}/>
                    <RadioButton value={RetentionPeriodStrategy.Forever} label="Keep all"/>
                    <RadioButton value={RetentionPeriodStrategy.Count} label="Keep a limited number"/>
                </RadioButtonGroup>
                {this.props.retentionPolicy.Strategy === RetentionPeriodStrategy.Count && (<ComponentRow>
                        <span>
                            <Text type="number" min={1} value={this.props.retentionPolicy.QuantityToKeep ? this.props.retentionPolicy.QuantityToKeep.toString() : ""} onChange={(x) => this.updateRetentionPolicy((pol) => (pol.QuantityToKeep = ParseHelper.safeParseInt(x)))} validate={minimum("Please provide a value greater than zero.", 1)}/>
                        </span>
                        <span>
                            <DeprecatedSelect value={this.props.retentionPolicy.Unit} onChange={(val) => this.updateRetentionPolicy((pol) => (pol.Unit = val as RetentionUnit))} items={[
                    { value: RetentionUnit.Days, text: "Days" },
                    { value: RetentionUnit.Items, text: "Runs" },
                ]}/>
                        </span>
                    </ComponentRow>)}
                <br />
                <Note>
                    Retention policies dictate how long runs are kept for. For more information please see <ExternalLink href="RetentionPolicies">retention policies</ExternalLink> documentation.
                </Note>
            </ExpandableFormSection>);
    }
    static displayName = "RunbookRetentionPolicyControlInternal";
}
interface RunbookRetentionPolicyControlProps {
    retentionPolicy: RunbookRetentionPolicy;
    onRetentionPolicyChange(policy: RunbookRetentionPolicy): void;
    defaultRetentionPolicy?: RunbookRetentionPolicy;
}
export function RunbookRetentionPolicyControl(props: RunbookRetentionPolicyControlProps) {
    return <RunbookRetentionPolicyControlInternal {...props}/>;
}
function retentionPolicySummary(retentionPolicy: RunbookRetentionPolicy): string {
    switch (retentionPolicy.Strategy) {
        case RetentionPeriodStrategy.Default:
            return "Use default retention policy";
        case RetentionPeriodStrategy.Forever:
            return "Keep all";
        case RetentionPeriodStrategy.Count:
            switch (retentionPolicy.Unit) {
                case RetentionUnit.Days:
                    return `Keep ${retentionPolicy.QuantityToKeep} ${pluralize("day", retentionPolicy.QuantityToKeep)} per environment`;
                case RetentionUnit.Items:
                    return `Keep ${retentionPolicy.QuantityToKeep} ${pluralize("run", retentionPolicy.QuantityToKeep)} per environment`;
            }
    }
}
function defaultRetentionPolicySummary(defaultPolicy: RunbookRetentionPolicy | undefined): string {
    switch (defaultPolicy?.Strategy) {
        case RetentionPeriodStrategy.Forever:
            return "(keep all)";
        case RetentionPeriodStrategy.Count:
            switch (defaultPolicy.Unit) {
                case RetentionUnit.Days:
                    return `(${defaultPolicy.QuantityToKeep} ${pluralize("day", defaultPolicy.QuantityToKeep)})`;
                case RetentionUnit.Items:
                    return `(${defaultPolicy.QuantityToKeep} ${pluralize("run", defaultPolicy.QuantityToKeep)})`;
            }
    }
    return "";
}
